import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { tokenStorage, isTokenExpired } from './auth';
import type {
  User,
  AuthResponse,
  LoginCredentials,
  RegisterData,
  Site,
  CreateSiteDto,
  UpdateSiteDto,
  DashboardStats,
  SiteStats,
  DailyStats,
  ApiError,
} from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Creates and configures the API client
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: `${API_URL}/api/v1`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth token
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = tokenStorage.getAccessToken();
      
      if (token) {
        // Check if token is expired and try to refresh
        if (isTokenExpired(token)) {
          const refreshToken = tokenStorage.getRefreshToken();
          if (refreshToken && !isTokenExpired(refreshToken)) {
            try {
              const response = await axios.post<AuthResponse>(
                `${API_URL}/api/v1/auth/refresh`,
                { refresh_token: refreshToken }
              );
              tokenStorage.setTokens(
                response.data.access_token,
                response.data.refresh_token
              );
              config.headers.Authorization = `Bearer ${response.data.access_token}`;
            } catch {
              tokenStorage.clearTokens();
              window.location.href = '/login';
            }
          } else {
            tokenStorage.clearTokens();
            window.location.href = '/login';
          }
        } else {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiError>) => {
      if (error.response?.status === 401) {
        tokenStorage.clearTokens();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const apiClient = createApiClient();

/**
 * API Client class with all methods
 */
export const api = {
  // ============ Auth ============
  
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // FastAPI OAuth2 expects form data
    const formData = new URLSearchParams();
    formData.append('username', credentials.email);
    formData.append('password', credentials.password);
    
    const response = await axios.post<AuthResponse>(
      `${API_URL}/api/v1/auth/login`,
      formData,
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );
    
    tokenStorage.setTokens(response.data.access_token, response.data.refresh_token);
    return response.data;
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await axios.post<AuthResponse>(
      `${API_URL}/api/v1/auth/register`,
      data
    );
    tokenStorage.setTokens(response.data.access_token, response.data.refresh_token);
    return response.data;
  },

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = tokenStorage.getRefreshToken();
    const response = await axios.post<AuthResponse>(
      `${API_URL}/api/v1/auth/refresh`,
      { refresh_token: refreshToken }
    );
    tokenStorage.setTokens(response.data.access_token, response.data.refresh_token);
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  async updateProfile(data: { full_name?: string }): Promise<User> {
    const response = await apiClient.patch<User>('/auth/me', data);
    return response.data;
  },

  async changePassword(data: { current_password: string; new_password: string }): Promise<void> {
    await apiClient.post('/auth/change-password', data);
  },

  async deleteAccount(): Promise<void> {
    await apiClient.delete('/auth/me');
    tokenStorage.clearTokens();
  },

  logout(): void {
    tokenStorage.clearTokens();
    window.location.href = '/login';
  },

  // ============ Sites ============
  
  async getSites(): Promise<Site[]> {
    const response = await apiClient.get<Site[]>('/sites/');
    return response.data;
  },

  async getSite(id: string): Promise<Site> {
    const response = await apiClient.get<Site>(`/sites/${id}`);
    return response.data;
  },

  async createSite(data: CreateSiteDto): Promise<Site> {
    const response = await apiClient.post<Site>('/sites/', data);
    return response.data;
  },

  async updateSite(id: string, data: UpdateSiteDto): Promise<Site> {
    const response = await apiClient.put<Site>(`/sites/${id}`, data);
    return response.data;
  },

  async deleteSite(id: string): Promise<void> {
    await apiClient.delete(`/sites/${id}`);
  },

  async regenerateKeys(id: string): Promise<Site> {
    const response = await apiClient.post<Site>(`/sites/${id}/regenerate-keys`);
    return response.data;
  },

  // ============ Analytics ============
  
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiClient.get<DashboardStats>('/analytics/dashboard');
    return response.data;
  },

  async getSiteStats(siteId: string, period: string = '7d'): Promise<SiteStats> {
    const response = await apiClient.get<SiteStats>(
      `/analytics/sites/${siteId}/stats`,
      { params: { period } }
    );
    return response.data;
  },

  async getDailyStats(siteId: string, days: number = 7): Promise<DailyStats[]> {
    const response = await apiClient.get<DailyStats[]>(
      `/analytics/sites/${siteId}/daily`,
      { params: { days } }
    );
    return response.data;
  },

  // ============ Stripe / Billing ============
  
  async createCheckoutSession(priceId: string): Promise<{ checkout_url: string; session_id: string }> {
    const response = await apiClient.post('/stripe/create-checkout-session', {
      price_id: priceId,
    });
    return response.data;
  },

  async createPortalSession(): Promise<{ portal_url: string }> {
    const response = await apiClient.post('/stripe/create-portal-session', {});
    return response.data;
  },

  async getSubscriptionStatus(): Promise<{
    tier: string;
    status: string;
    stripe_subscription_id: string | null;
    stripe_customer_id: string | null;
    current_period_end: string | null;
    verifications_used: number;
    verifications_limit: number;
  }> {
    const response = await apiClient.get('/stripe/subscription-status');
    return response.data;
  },

  async getStripePrices(): Promise<{
    prices: {
      [key: string]: {
        id: string;
        name: string;
        price: number;
        verifications: number;
        sites: number;
      };
    };
  }> {
    const response = await apiClient.get('/stripe/prices');
    return response.data;
  },
};

export default api;
